import dayjs from "dayjs"

export const transformLink = (data: any = {}) : IResponseApiLink => {
  const merchant = transformMerchant(data);
  
  if (Object.keys(data).length === 0) {
    merchant.webhooks = [];
  }

  return {
    id: data?.id,
    name: data?.name || '',
    merchant,
    order: transformOrder(data),
    capabilities: transformCapabilities(data) || [],
    lang: data?.company?.default_locale,
    zeroInterestinstallments: data?.template?.zero_interest_installment ?? null,
    saveCard: getSaveCardValue(data.capabilities),
    googlePaySettings: transformGooglePaySettings(data),
  }
}

const getSaveCardValue = (capabilities: { payment_method: string, saveCard?: boolean }[] | undefined) => {
  const cardMethod = capabilities?.find(item => item.payment_method === 'card')

  if (cardMethod) return cardMethod.saveCard || false;
  return false;
}

export const transformMerchant = (data: any = {}) : IMerchant => {
  const description = data?.company?.description === 'null' ? '' : data?.company?.description || '';
  
  return {
    publicKey: data?.company?.public_key || '',
    logo: data?.company?.logo || '',
    name: data?.company?.name || '',
    description,
    theme: transformTheme(data),
    policy: transformPolicy(data),
    webhooks: data?.company?.webhooks || undefined,
  }
}

export const transformTheme = (data: any = {}) : TypeTheme => {
  return {
    backgroundColor: data?.company?.background_color || '',
    fontColor: data?.company?.text_color || '',
  }
}

export const transformPolicy = (data: any = {}) : TPolicy => {
  return {
    refund: data?.company?.refund_policy || '',
  }
}

export const transformOrder = (data: any = {}) : IOrder => {
  return {
    state: data?.state || '',
    amount: data?.amount || 0,
    currency: data?.currency || '',
    expiryDate: (() => {
      return data?.expired_at
        ? dayjs(data.expired_at).format('D MMM YYYY, HH:mm:ss')
        : ''
    })(),
    additional: data?.additional || '',
    features: data?.features || {},
    meta: transformLinkFeatures(data?.features || {}),
  }
}

export const transformCapability = (item: any = {}) : ICapability => {
  return {
    paymentMethod: item?.payment_method || '',
    installmentTerms: item?.installment_terms || [],
  }
}

export const transformCapabilities = (data: any = {}) : ICapability[] | undefined => {
  if (!data?.capabilities) return undefined;
  
  return data.capabilities.map((item: any) => ({
    paymentMethod: item.payment_method || '',
    installmentTerms: item.installment_terms || []
  }));
}

export const transformLinkFeatures = (data: any = {}) => {
  return Object.keys(data).reduce((acc: any, cur: string) => {
    const excludedKeys = [
      'platform',
      'link_id',
      'link_name',
      'transaction_id',
      'additional',
      'client_id',
    ]
    if(!excludedKeys.includes(cur)) {
      acc.push({ name: cur, value: data[cur] })
    }
    return acc
  }, [])
}


export const transformGooglePaySettings = (data: any = {}): IGooglePaySettings => {
  const capabilities: ({ payment_method: string }[] | undefined) = data?.capabilities;
  const hasGooglePayCapability: boolean = !!(capabilities?.some((item) => item.payment_method === 'googlepay'));

  const company = data?.company || {};
  const {
      googlepay_mid,
      googlepay_request_billing_address,
      googlepay_request_phone_number
    } = company;

  if (googlepay_mid && hasGooglePayCapability) {
    return {
      googlepayRequestBillingAddress: googlepay_request_billing_address || false,
      googlepayRequestPhoneNumber: googlepay_request_phone_number || false,
      googlepayMerchantId: googlepay_mid,
    }
  }

  return {
    googlepayRequestBillingAddress: false,
    googlepayRequestPhoneNumber: false,
    googlepayMerchantId: '',
  }
}

export interface IMerchant {
  publicKey: string;
  logo: string;
  name: string;
  description: string;
  theme: TypeTheme;
  policy: TPolicy;
  webhooks: string[];
}

export interface IOrder {
  state: string;
  amount: number;
  currency: string;
  expiryDate: string;
  additional: string;
  features: object;
  meta: {
    name: string;
    value: string;
  }[];
}

export interface ICapability {
  paymentMethod: string;
  installmentTerms: number[];
}

export type TypeTheme = {
  backgroundColor: string;
  fontColor: string;
}

export type TPolicy = {
  refund: string;
}

export interface IResponseApiLink {
  id: number;
  name: string;
  merchant: IMerchant;
  order: IOrder;
  capabilities: ICapability[];
  lang: string;
  zeroInterestinstallments: boolean | null;
  saveCard: boolean;
  googlePaySettings: IGooglePaySettings;
}

export interface IPaymentFormConfiguration {
  textForFooter:  string
}

export interface IGooglePaySettings {
  googlepayRequestBillingAddress: boolean;
  googlepayRequestPhoneNumber: boolean;
  googlepayMerchantId: string;
}
